import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote } from '../components/ui';

const TEMPLATES = [
  { key: 'followup', label: 'Proposal follow-up',
    subject: 'Following up on your roofing proposal',
    body: (n) => `Hi ${n},\n\nI wanted to follow up on the proposal we sent over. Do you have any questions, or is there anything you'd like us to adjust?\n\nWe'd be glad to walk you through it whenever suits you.` },
  { key: 'checkin', label: 'Check-in after job',
    subject: 'How did everything turn out?',
    body: (n) => `Hi ${n},\n\nNow that the work is finished, I wanted to check that everything looks right and you're happy with the result. If anything needs attention, just reply and we'll take care of it.` },
  { key: 'payment', label: 'Payment reminder',
    subject: 'A friendly reminder about your invoice',
    body: (n) => `Hi ${n},\n\nA quick reminder that your invoice is still open. If you've already sent payment, thank you — please ignore this note. Otherwise, let me know if you have any questions.` },
  { key: 'blank', label: 'Blank', subject: '', body: () => '' },
];

export default function ComposeEmail() {
  const [params] = useSearchParams();
  const projectId = params.get('project');
  const customerId = params.get('customer');
  const navigate = useNavigate();
  const { toast, run } = useApp();

  const { data, error, loading, reload } = useAsync(async () => {
    if (projectId) {
      const p = await api.get('/projects/' + projectId);
      return { customer: p.customer, project: p };
    }
    if (customerId) return { customer: await api.get('/customers/' + customerId), project: null };
    return { customer: null, project: null };
  }, [projectId, customerId]);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachProposal, setAttachProposal] = useState(false);
  const [attachAgreement, setAttachAgreement] = useState(false);
  const [busy, setBusy] = useState(false);

  const applyTemplate = (t, customer) => {
    setSubject(t.subject);
    setMessage(t.body(customer?.name?.split(' ')[0] || 'there'));
  };

  useEffect(() => {
    if (!data) return;
    setTo(data.customer?.email || '');
    applyTemplate(TEMPLATES[0], data.customer);
  }, [data]);

  if (loading) return <Spinner label="Loading" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;

  const { customer, project } = data;
  const back = project ? `/project/${project.id}` : customer ? `/customer/${customer.id}` : '/settings/emails';

  const onSend = (e) => {
    e.preventDefault();
    run(async () => {
      setBusy(true);
      try {
        const r = await api.post('/emails/send', {
          to, subject, message, projectId: project?.id,
          attachProposal: attachProposal && !!project?.proposal,
          attachAgreement: attachAgreement && !!project?.agreement,
        });
        const failed = r.emails.filter(x => !x.delivered);
        if (failed.length) toast(`${failed.length} email${failed.length > 1 ? 's' : ''} failed: ${failed[0].error || 'unknown error'}`);
        else { toast(`Email sent to ${r.emails.length} ${r.emails.length === 1 ? 'person' : 'people'}`); navigate(back); }
      } finally { setBusy(false); }
    });
  };

  return (
    <>
      <PageHeader title="Send email" sub={customer?.name || 'Follow-ups and other messages'} back={back} />
      <form className="view" onSubmit={onSend}>
        <Card>
          <SectionTitle sub="Start from a template, then edit freely.">Message</SectionTitle>
          <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
            <div className="btn-row">
              {TEMPLATES.map(t => (
                <button key={t.key} type="button" className="btn sm" onClick={() => applyTemplate(t, customer)}>{t.label}</button>
              ))}
            </div>
            <Field label="To" id="em-to"
              hint="Anyone — customers, team or outside contacts. Separate several addresses with commas; each gets their own copy.">
              <input id="em-to" className="fi" type="text" inputMode="email" required placeholder="name@example.com"
                value={to} onChange={e => setTo(e.target.value)} />
            </Field>
            <Field label="Subject" id="em-subject">
              <input id="em-subject" className="fi" required maxLength={200} value={subject} onChange={e => setSubject(e.target.value)} />
            </Field>
            <Field label="Message" id="em-msg">
              <textarea id="em-msg" className="fi" rows={9} required maxLength={5000} value={message} onChange={e => setMessage(e.target.value)} />
            </Field>
          </div>
        </Card>

        {project?.proposal || project?.agreement ? (
          <Card>
            <SectionTitle sub={`From ${project.number}. Attached as PDFs.`}>Attachments</SectionTitle>
            <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {project.proposal ? (
                <label className="row" style={{ gap: 'var(--s-2)' }}>
                  <input type="checkbox" checked={attachProposal} onChange={e => setAttachProposal(e.target.checked)} />
                  Proposal #{project.proposal.number}
                </label>
              ) : null}
              {project.agreement ? (
                <label className="row" style={{ gap: 'var(--s-2)' }}>
                  <input type="checkbox" checked={attachAgreement} onChange={e => setAttachAgreement(e.target.checked)} />
                  Agreement
                </label>
              ) : null}
            </div>
          </Card>
        ) : null}

        <button className="btn pri" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send email'}</button>
      </form>
    </>
  );
}
